export const CATEGORY_NAV_LINKS = [
  { label: "New Arrivals", to: "/category/new-arrivals" },
  { label: "Trending", to: "/category/trending" },
];

export const SHOPPER_NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "All Products", to: "/products" },
  {
    label: "Categories",
    to: "/category",
    children: CATEGORY_NAV_LINKS,
  },
];

export const FOOTER_LINK_GROUPS = [
  {
    title: "Support",
    links: [
      { label: "Help Center", to: "/help-center" },
      { label: "Contact Us", to: "/contact-us" },
      { label: "Shipping Information", to: "/shipping-information" },
      { label: "Returns & Refunds", to: "/returns-and-refunds" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About Us", to: "/about" },
      { label: "Careers & Jobs", to: "/careers" },
      { label: "Terms & Conditions", to: "/terms-and-conditions" },
      { label: "Privacy Policy", to: "/privacy-policy" },
    ],
  },
  {
    title: "Partners",
    links: [
      { label: "Sell on Apparify", to: "/sell-on-apparify" },
      { label: "Business Portal", to: "/business/login" },
      { label: "Supplier Portal", to: "/supplier/login" },
      { label: "Admin Portal", to: "/admin/login" },
    ],
  },
];

export const SITE_PAGE_CONTENT = {
  about: {
    eyebrow: "About Apparify",
    title: "A modern apparel shopping experience built for stores in Cavite.",
    intro:
      "Apparify helps shoppers browse apparel from participating stores in Cavite, place orders with confidence, and stay updated from checkout to delivery. The storefront is connected to the same platform that supports store operations, so shopping activity, fulfillment updates, and account tools stay in sync.",
    sections: [
      {
        title: "Built for local apparel retail",
        body: [
          "The platform is shaped around the needs of apparel stores in Cavite and the customers who buy from them online. It focuses on clear product browsing, reliable checkout, and a smoother post-purchase experience.",
          "Instead of treating the storefront as a separate layer, it connects shopper activity with the tools stores use to manage products, orders, and fulfillment.",
        ],
      },
      {
        title: "One connected experience",
        list: [
          "Browse apparel listings, view options, and shop from participating stores through a mobile-friendly storefront.",
          "Keep track of payment results, saved addresses, purchase history, and notifications in one account experience.",
          "Use seller messaging when you need product clarification, fulfillment updates, or help after ordering.",
        ],
      },
      {
        title: "Clearer product viewing",
        body: [
          "Some stores may maintain 3D-ready catalog assets to support richer product presentation. That makes it easier to review selected apparel items from more than one angle when enhanced viewing content is available.",
        ],
      },
      {
        title: "Designed to stay practical",
        body: [
          "The platform is mobile-first for everyday shoppers, while the broader system also supports store-side planning and data-informed decisions behind the scenes. The goal is a storefront that feels simple to use while staying connected to real operational work.",
        ],
      },
    ],
  },
  contact: {
    eyebrow: "Contact & Support",
    title: "Get help with your account, orders, or shopper experience.",
    intro:
      "Use the support path that matches your concern. Your orders, notifications, and seller conversations are already connected, which makes it easier to get help without repeating the same details over and over.",
    sections: [
      {
        title: "Best places to start",
        list: [
          "Open My Purchases to review order status, seller grouping, payment updates, and delivery details.",
          "Use seller chat when your concern is tied to a specific store, product option, or fulfillment question.",
          "Check Notifications first for payment confirmations, order movement, or account activity.",
        ],
      },
      {
        title: "When platform support is the better fit",
        body: [
          "Platform support is best for account access issues, storefront problems, checkout concerns, or anything that looks like a system-level issue rather than a store-specific one.",
          "If the issue is about sizing, item availability, delivery coordination, or store handling, seller chat is still the fastest path whenever messaging is available for that order or listing.",
        ],
      },
      {
        title: "Helpful details to prepare",
        list: [
          "Your order reference number, if the concern is purchase-related.",
          "The product name, quantity, or variation involved.",
          "A short description of what happened during checkout, payment, or delivery.",
          "Screenshots if something on the page did not load or behave correctly.",
        ],
      },
    ],
  },
  help: {
    eyebrow: "Help Center",
    title: "Quick answers for browsing, checkout, delivery, and account use.",
    intro:
      "These help notes focus on the shopper experience that is already available today, including storefront browsing, checkout, purchase tracking, notifications, and seller messaging.",
    faq: [
      {
        question: "How do I check the status of my order?",
        answer:
          "Go to My Purchases in your shopper account. Orders are grouped clearly so you can review payment state, seller details, totals, and the latest order movement in one place.",
      },
      {
        question: "How do I know if my payment went through?",
        answer:
          "After checkout, the payment result flow updates your shopper experience, and the order confirmation page shows the payment state again. You can also reopen the same order from My Purchases.",
      },
      {
        question: "Can I still contact a seller after ordering?",
        answer:
          "Yes. If messaging is available for that store, you can continue using chat after ordering to ask about variations, fulfillment updates, or delivery-related concerns.",
      },
      {
        question: "What address is used for my order?",
        answer:
          "Checkout uses the saved address you selected. The order keeps its own delivery snapshot so your confirmation and purchase history stay accurate even if you update your saved addresses later.",
      },
      {
        question: "Where do delivery updates appear?",
        answer:
          "Delivery and fulfillment updates appear through your order flow, notifications, and any shipment details already attached to the order. If a final courier estimate is not available yet, the page falls back safely instead of showing broken information.",
      },
      {
        question: "Can products support richer viewing?",
        answer:
          "Yes. When a store provides enhanced catalog assets, selected products can support richer viewing, including 3D-ready presentation for a clearer look at the item.",
      },
    ],
  },
  terms: {
    eyebrow: "Terms & Conditions",
    title: "Guidelines for using the shopper side of the platform.",
    intro:
      "These terms explain how customers are expected to use the shopper-facing parts of the platform, including browsing, ordering, messaging, and account features.",
    sections: [
      {
        title: "Using your shopper account",
        list: [
          "Provide accurate account, contact, and delivery details when using protected shopper routes such as checkout and purchase management.",
          "Use the platform only for lawful browsing, purchasing, communication, and account activity.",
          "Product availability, pricing visibility, and fulfillment timing may vary by store, listing, and current order state.",
        ],
      },
      {
        title: "Orders and fulfillment",
        list: [
          "Orders are created from live cart selections and remain subject to stock checks, payment outcome, and seller processing.",
          "Payment state, order state, and shipment progress may update at different times as the order moves through checkout and fulfillment.",
          "Store-specific shipping methods, delivery fees, and order notes may appear per seller group when those details are recorded.",
        ],
      },
      {
        title: "Access and responsible use",
        list: [
          "Some features, including purchases, cart, checkout, and messaging, require an authenticated account.",
          "Abuse of seller communication, harmful activity, or misuse of the platform may lead to access restrictions.",
          "Features may continue to evolve as the platform expands for shoppers and participating apparel stores.",
        ],
      },
    ],
  },
  privacy: {
    eyebrow: "Privacy Policy",
    title: "How shopper information is used across orders, support, and account tools.",
    intro:
      "This privacy summary explains the information needed to run the shopper experience while keeping order records, delivery details, and account activity understandable from purchase to fulfillment.",
    sections: [
      {
        title: "Information used for purchases",
        body: [
          "The platform uses account details, item selections, saved addresses, payment updates, and order timestamps to process purchases and display accurate confirmation data.",
          "Each order keeps its own delivery snapshot so the record remains readable even if you later update the addresses saved in your account.",
        ],
      },
      {
        title: "Information used for communication",
        body: [
          "Notifications and seller messaging help shoppers receive updates and ask questions with the right order context.",
          "When support is needed, order references, timestamps, and related account details may be reviewed to understand the issue and guide the right response.",
        ],
      },
      {
        title: "Account controls",
        list: [
          "Shoppers can manage profile details, addresses, and notification settings through the account area.",
          "Privacy-related account actions remain tied to the controls that are currently available in the shopper experience.",
        ],
      },
    ],
  },
  shipping: {
    eyebrow: "Shipping Information",
    title: "How delivery works after checkout.",
    intro:
      "Shipping depends on the items in your order, the stores fulfilling them, and the delivery details recorded during checkout or fulfillment.",
    sections: [
      {
        title: "Why orders may be grouped by store",
        body: [
          "Orders can be grouped by seller so you can see which store is preparing which items. That is why shipping fees, notes, and seller totals may appear per group instead of as one flat block.",
          "When checkout records delivery fees and shipping methods, those same details carry through to confirmation and purchase pages so you can review the same breakdown after ordering.",
        ],
      },
      {
        title: "Estimated delivery timing",
        body: [
          "If shipment records already contain estimated delivery dates, the order confirmation page shows them directly. If those details are not available yet, the shopper view falls back gracefully while the store prepares the order.",
          "This keeps the delivery summary useful without showing misleading countdowns or placeholder dates.",
        ],
      },
      {
        title: "Where to check progress",
        list: [
          "Use My Purchases for the most complete order summary.",
          "Watch Notifications for payment and fulfillment updates.",
          "Use seller chat when the question depends on a specific store or delivery arrangement.",
        ],
      },
    ],
  },
  returns: {
    eyebrow: "Returns, Refunds & Cancellations",
    title: "What to do when an order needs follow-up after checkout.",
    intro:
      "Return, refund, and cancellation handling depends on the payment state, fulfillment progress, and the current order status recorded for the purchase.",
    sections: [
      {
        title: "Before fulfillment moves forward",
        body: [
          "If the order is still early in the process, cancellation may still be possible depending on the payment result and seller handling. My Purchases is the best place to review the current state first.",
        ],
      },
      {
        title: "After shipment or delivery",
        body: [
          "If an order is already being prepared, shipped, or delivered, review the order details first and then contact the relevant seller when the concern is tied to store handling, item condition, or delivery coordination.",
        ],
      },
      {
        title: "How updates appear in your account",
        body: [
          "When return- or refund-related statuses are part of the order record, they appear in shopper order views so you can follow the issue without relying only on outside communication.",
        ],
      },
    ],
  },
  careers: {
    eyebrow: "Careers & Job Postings",
    title: "Help build a better apparel commerce experience in Cavite.",
    intro:
      "This careers portal is where project-aligned opportunities are published for platform support, commerce operations, and shopper-facing product work. Roles are shown only when they are approved and ready for applications.",
    sections: [
      {
        title: "How this portal works",
        body: [
          "Career opportunities are posted here when hiring is active. If no active role listings are visible, there are currently no open positions accepting applications.",
          "Every posting is expected to include a clear role scope, required skills, and the application path so applicants can evaluate fit before submitting.",
        ],
      },
      {
        title: "Typical focus areas",
        list: [
          "Shopper experience support, including account, checkout, and post-purchase workflows.",
          "Business-side operations support tied to inventory, order handling, and seller communication.",
          "Platform and data quality support that helps keep storefront and operational records reliable.",
        ],
      },
      {
        title: "For participating stores and business operators",
        body: [
          "The careers portal is structured to support future hiring expansion connected to the same business ecosystem used by participating stores.",
          "If you are a business operator, use the Business Portal to manage your organization context first, then coordinate role posting workflows as those hiring features are enabled.",
        ],
      },
    ],
    faq: [
      {
        question: "Where can I find active job openings?",
        answer:
          "Active openings appear on this careers page when they are published. If no role cards are shown, hiring is not currently open for this cycle.",
      },
      {
        question: "Can stores publish hiring needs through this platform?",
        answer:
          "The structure is designed for future store-aligned hiring expansion. Business operators should keep their portal membership and business profile up to date so role-posting workflows can be activated safely when available.",
      },
    ],
  },
};
